import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import _, { isEqual } from 'lodash';
import {
  MyPurchaseForm,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { SourcingStatusId } from 'src/app/shared/models/database-models/SourcingStatus';
import { updateItemsSourcingStatus } from 'src/app/core/store/form.actions';

export interface Vendor {
  vendorNumber: string;
  vendorName: string;
}

// Status flag to backend when posting to API
export enum GroupStatus {
  // Added during session
  NEW = 'NEW',
  // Updated during session
  UPDATED = 'UPDATED',
  // Deleted during session, not yet saved to database
  DELETED = 'DELETED',
  // Permanently inactive in database, not modifiable
  PERSISTED_INACTIVE = 'PERSISTED_INACTIVE',
  // Saved as active in database, not yet modified during session
  EXISTING = 'EXISTING',
}

export interface TaggedGroup {
  taggedGroupId: number | null;
  itemNumbers: number[];
  vendors: Vendor[] | null;
  sourcingStatusId?: number;
  purchaseExecutive: string;
  groupStatus: GroupStatus;
}

export interface TaggedGroups {
  MPDocumentNumber: string;
  taggedGroups: TaggedGroup[];
}

const INITIAL_TAGGED_GROUPS: TaggedGroups = {
  MPDocumentNumber: '',
  taggedGroups: [],
};

export const NEW_TAG_GROUP: TaggedGroup = {
  taggedGroupId: null,
  itemNumbers: [],
  vendors: [],
  sourcingStatusId: SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED,
  purchaseExecutive: '', // Will be filled in during runtime
  groupStatus: GroupStatus.NEW,
};

export enum InvalidTaggedGroupReason {
  EMPTY_ITEM_NUMBERS = 'EMPTY_ITEM_NUMBERS',
  TAGGED_TO_MISSING_DELETED_ITEM = 'TAGGED_TO_MISSING_DELETED_ITEM',
  ITEM_ASSIGNED_TO_ANOTHER_PE = 'ITEM_ASSIGNED_TO_ANOTHER_PE',
  MISSING_SOURCING_STATUS = 'MISSING_SOURCING_STATUS',
  MISSING_VENDORS = 'MISSING_VENDORS',
  TAGGED_TO_MISSING_DELETED_VENDOR = 'TAGGED_TO_MISSING_DELETED_VENDOR',
}

export interface InvalidTaggedGroup {
  taggedGroup: TaggedGroup;
  invalidReason: InvalidTaggedGroupReason;
}

export interface TaggedGroupsForPosting {
  invalidTaggedGroups: InvalidTaggedGroup[];
  validTaggedGroups: TaggedGroup[];
}

export type DisplayItemNumberToSourcingStatusIdMap = Map<number, number>;

export enum TaggingValidationType {
  DUPLICATED_ITEM_SET = 'DUPLICATED_ITEM_SET',
  PURCHASE_EXECUTIVE_TAG_CONFLICT = 'PURCHASE_EXECUTIVE_TAG_CONFLICT',
  DUPLICATED_VENDOR_SET = 'DUPLICATED_VENDOR_SET',
  EXISTING_VENDOR_TAGGED = 'EXISTING_VENDOR_TAGGED',
  REASSIGNMENT_UNSAVED_TAG = 'REASSIGNMENT_UNSAVED_TAG',
  DELETE_VENDOR_OWN_TAG_CONFLICT = 'DELETE_VENDOR_OWN_TAG_CONFLICT',
  DELETE_VENDOR_OTHER_PURCHASE_EXECUTIVE_TAG_CONFLICT = 'DELETE_VENDOR_OTHER_PURCHASE_EXECUTIVE_TAG_CONFLICT',
}

export interface TaggingValidation {
  type: TaggingValidationType;
  incomingTaggedGroup?: TaggedGroup;
  conflictingTaggedGroups?: TaggedGroup[];
  existingTaggedGroupRef?: TaggedGroup;
  displayAffected?: string;
}

export enum TaggingResolutionChoice {
  MERGE = 'MERGE',
  REPLACE = 'REPLACE',
  REMOVE_AFFECTED = 'REMOVE_AFFECTED',
}

export type TaggingResolutionChoices = {
  [TaggingValidationType.EXISTING_VENDOR_TAGGED]?: TaggingResolutionChoice.REMOVE_AFFECTED;
  [TaggingValidationType.DUPLICATED_VENDOR_SET]?:
    | TaggingResolutionChoice.MERGE
    | TaggingResolutionChoice.REPLACE;
};

export enum TaggingValidationSuccessType {
  PASSED_ADD = 'PASSED_ADD',
  PASSED_UPDATE = 'PASSED_UPDATE',
}

export interface TaggingValidationSuccess {
  type: TaggingValidationSuccessType;
}

export interface TaggingImmediateValidationResult {
  isSuccess: boolean;
  validationType?: TaggingValidationType;
}

interface TaggedGroupVendorNum extends TaggedGroup {
  vendorNumbers: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class TagItemVendorService implements OnDestroy {
  private loggedInUsername?: string;
  private form: MyPurchaseForm | null = null;
  destroy$: Subject<boolean> = new Subject<boolean>();

  private taggedGroups = new BehaviorSubject<TaggedGroups>(
    INITIAL_TAGGED_GROUPS
  );
  public taggedGroups$ = this.taggedGroups.asObservable();

  private tempTagGroup = new BehaviorSubject<TaggedGroup | null>(null);
  public tempTagGroup$ = this.tempTagGroup.asObservable();

  private taggingValidation = new Subject<TaggingValidation>();
  public taggingValidation$ = this.taggingValidation.asObservable();

  private taggingValidationSuccess = new Subject<TaggingValidationSuccess>();
  public taggingValidationSuccess$ =
    this.taggingValidationSuccess.asObservable();

  constructor(
    private store: Store<{ user: UserInformation; form: MyPurchaseForm }>
  ) {
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.loggedInUsername = user.userName;
      });

    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        this.form = form;
      });

    this.taggedGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ MPDocumentNumber, taggedGroups }: TaggedGroups) => {
        if (MPDocumentNumber && this.loggedInUsername) {
          // Update Store -> MyPurchaseForm.ItemList -> each item SourcingStatusId, when any tagging change
          this.store.dispatch(
            updateItemsSourcingStatus({
              displayItemNumberToSourcingStatusIdMap:
                this.getItemSourcingStatusMap(taggedGroups),
              purchaseExecutive: this.loggedInUsername,
            })
          );
        }
      });

    combineLatest([
      this.store.select('form'),
      this.taggedGroups$,
    ])
      // prevent unwanted renders by checking if documentNo or TaggedGroups changes
      .pipe(distinctUntilChanged((prev:[MyPurchaseForm, TaggedGroups], curr) => 
        prev[0].documentNo == curr[0].documentNo
        && prev[1].MPDocumentNumber == curr[1].MPDocumentNumber
        && this.deepCompareArrays(prev[0].TaggedGroups, curr[0].TaggedGroups) 
        && this.deepCompareArrays(prev[1].taggedGroups, curr[1].taggedGroups) 
      ))
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([form, taggedGroupsObj]: [
          MyPurchaseForm,
          TaggedGroups,
        ]) => {
          if (
            form.documentNo &&
            form.documentNo === taggedGroupsObj.MPDocumentNumber
          ) {
            const { taggedGroups } = taggedGroupsObj;

            // if taggedGroups has null id while form.TaggedGroups doesn't, atempt to retrieve the id
            if (taggedGroups.length == form.TaggedGroups?.length
              && taggedGroups.some((item) => !item.taggedGroupId) && form.TaggedGroups?.some((item) => item.taggedGroupId)) {
              let filteredTaggedGroups = this.getActiveTaggedGroups(taggedGroups);

              filteredTaggedGroups = filteredTaggedGroups.map((item) => {

                const savedTag = form.TaggedGroups?.find((tag) => tag.groupStatus === GroupStatus.EXISTING && item.groupStatus === GroupStatus.NEW 
                && this.compareArrays(tag.itemNumbers, item.itemNumbers) && this.deepCompareArrays(tag.vendors, item.vendors) 
                && isEqual(tag.purchaseExecutive, item.purchaseExecutive))!;

                if (savedTag) {
                  item.taggedGroupId = savedTag.taggedGroupId ?? item.taggedGroupId;
                  item.groupStatus = savedTag.groupStatus ?? item.groupStatus;
                }

                return item;
              });

              this.taggedGroups.next({
                MPDocumentNumber: form.documentNo,
                taggedGroups: filteredTaggedGroups,
              });
            }

            // if count isn't the same and both have taggedGoupId, attempt to update TaggedGroups
            if (taggedGroups.length != form.TaggedGroups?.length
              && !taggedGroups.some((item) => !item.taggedGroupId) && !form.TaggedGroups?.some((item) => !item.taggedGroupId)) {
              let filteredTaggedGroups = this.getActiveTaggedGroups(taggedGroups);

              this.taggedGroups.next({
                MPDocumentNumber: form.documentNo,
                taggedGroups: filteredTaggedGroups,
              });
            }
          }
        }
      );
  }

  public get taggedGroupsValue() {
    return this.taggedGroups.value;
  }

  public setTaggedGroups(
    MPDocumentNumber: string,
    newTaggedGroups: TaggedGroups['taggedGroups']
  ) {
    const sortedTaggedGroups = _.sortBy(
      // Sort arrays inside a group
      newTaggedGroups.map(this.sortInsideItemVendor),
      (group: TaggedGroup) => {
        // Sort order of all tagged groups
        return [group.purchaseExecutive, group.itemNumbers.join(',')];
      }
    );

    // Put current user's tagged groups in front of other PEs
    const currentUserTaggedGroups: TaggedGroups['taggedGroups'] = [];
    const otherUsersTaggedGroups: TaggedGroups['taggedGroups'] = [];

    for (const group of sortedTaggedGroups) {
      if (this.isCurrentPETaggedGroup(group)) {
        currentUserTaggedGroups.push(group);
      } else {
        otherUsersTaggedGroups.push(group);
      }
    }

    const taggedGroups = {
      MPDocumentNumber: MPDocumentNumber,
      taggedGroups: [...currentUserTaggedGroups, ...otherUsersTaggedGroups],
    };
    this.taggedGroups.next(taggedGroups);

    return taggedGroups;
  }

  public setTaggedGroupsIfDocNumberChanged(
    MPDocumentNumber: string,
    newTaggedGroups: TaggedGroups['taggedGroups']
  ) {
    // const oldMPDocumentNumber = this.taggedGroups.value.MPDocumentNumber;

    // if (oldMPDocumentNumber !== MPDocumentNumber) {
      return this.setTaggedGroups(MPDocumentNumber, newTaggedGroups);
    // }

    // return null;
  }

  public addTaggedGroup(
    newTaggedGroup: TaggedGroup,
    resolutionChoices?: TaggingResolutionChoices
  ): TaggingImmediateValidationResult {
    const { MPDocumentNumber, taggedGroups: otherTaggedGroups } =
      this.taggedGroups.value;

    const { isSuccess, validationType, resultingTaggedGroups } =
      this.validateResolveIncomingTaggedGroup(
        resolutionChoices,
        newTaggedGroup,
        otherTaggedGroups
      );

    if (!isSuccess || !resultingTaggedGroups) {
      return { isSuccess: false, validationType: validationType };
    }

    // Emit new resulting tagged groups
    this.taggedGroups.next({
      MPDocumentNumber,
      taggedGroups: resultingTaggedGroups,
    });

    // Emit validation and add to resulting array success
    this.taggingValidationSuccess.next({
      type: TaggingValidationSuccessType.PASSED_ADD,
    });

    return { isSuccess: true };
  }

  public updateTaggedGroup(
    existingTaggedGroupRef: TaggedGroup,
    newTaggedGroup: TaggedGroup,
    resolutionChoices?: TaggingResolutionChoices
  ): TaggingImmediateValidationResult {
    const { MPDocumentNumber, taggedGroups } = this.taggedGroups.value;

    // Get other tagged groups excluding existingTaggedGroup and newTaggedGroup
    let otherTaggedGroups = this.getOtherTaggedGroups(
      existingTaggedGroupRef,
      taggedGroups
    );

    const { isSuccess, validationType, resultingTaggedGroups } =
      this.validateResolveIncomingTaggedGroup(
        resolutionChoices,
        newTaggedGroup,
        otherTaggedGroups,
        existingTaggedGroupRef
      );

    if (!isSuccess || !resultingTaggedGroups) {
      return { isSuccess: false, validationType: validationType };
    }

    // Emit new resulting tagged groups
    this.taggedGroups.next({
      MPDocumentNumber,
      taggedGroups: resultingTaggedGroups,
    });

    // Emit validation and update to resulting array success
    this.taggingValidationSuccess.next({
      type: TaggingValidationSuccessType.PASSED_UPDATE,
    });

    return { isSuccess: true };
  }

  private validateResolveIncomingTaggedGroup(
    resolutionChoices: TaggingResolutionChoices = {},
    incomingTaggedGroup: TaggedGroup,
    otherTaggedGroups: TaggedGroups['taggedGroups'],
    existingTaggedGroupRef?: TaggedGroup
  ) {
    // Get other active tagged groups with additional vendorNumbers
    const activeGroups = this.computeVendorNumbersInTaggedGroups(
      this.getActiveTaggedGroups(otherTaggedGroups)
    );
    const inactiveGroups = this.getInactiveTaggedGroups(otherTaggedGroups);

    // Sort first, to compare inside arrays (itemNumbers & vendorNumbers) later against other groups
    const [incoming] = this.computeVendorNumbersInTaggedGroups([
      this.sortInsideItemVendor(incomingTaggedGroup),
    ]);
    const isIncomingVendorTagged =
      incoming.sourcingStatusId ===
      SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED;

    // Arrays inside are already sorted
    let { currentPE, otherPEs } =
      this.splitTaggedGroupsByPEs<TaggedGroupVendorNum[]>(activeGroups);

    /** Against other Purchase Executives tagged groups */

    // Incoming vendors exactly match another Purchase Executive tagged group vendors
    if (incoming.vendors && incoming.vendors.length > 0) {
      const otherPEGroupsWithSameVendors = otherPEs.filter((other) => {
        return _.isEqual(other.vendorNumbers, incoming.vendorNumbers);
      });

      if (otherPEGroupsWithSameVendors.length > 0) {
        const validationType =
          TaggingValidationType.PURCHASE_EXECUTIVE_TAG_CONFLICT;
        this.taggingValidation.next({
          type: validationType,
          incomingTaggedGroup: incomingTaggedGroup,
          existingTaggedGroupRef: existingTaggedGroupRef,
          conflictingTaggedGroups: otherPEGroupsWithSameVendors,
        });

        return { isSuccess: false, validationType: validationType };
      }
    }

    /** Against own existing tagged groups */

    // If items are selected and vendors are to be tagged,
    // check if there are other own tagged groups with exact match of item numbers, irregardless of vendors tagged
    if (isIncomingVendorTagged && incoming.itemNumbers.length > 0) {
      const groupsWithSameItems = currentPE.filter((other) => {
        return (
          other.sourcingStatusId ===
            SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
          _.isEqual(other.itemNumbers, incoming.itemNumbers)
        );
      });

      if (groupsWithSameItems.length > 0) {
        const validationType = TaggingValidationType.DUPLICATED_ITEM_SET;
        this.taggingValidation.next({
          type: validationType,
          incomingTaggedGroup: incomingTaggedGroup,
          existingTaggedGroupRef: existingTaggedGroupRef,
          conflictingTaggedGroups: groupsWithSameItems,
        });

        return { isSuccess: false, validationType: validationType };
      }
    }

    // Include currentPE and otherPEs
    let tempActiveGroups = _.cloneDeep(activeGroups);
    // If true, incoming has been merged into existing tagged groups, no need to add separately
    let isNotAddIncoming = false;

    // If incoming tagged group is not tagging to any vendor, but incoming items selected already exist in other own tagged groups with vendors tagged
    // Validation to confirm with user if overlapping items should be removed from existing tagged groups with vendors tagged
    if (!isIncomingVendorTagged && incoming.itemNumbers.length > 0) {
      const allOverlappingItemNumbers: number[] = [];
      const groupsOverlappingItemsVendorTagged = currentPE.filter((other) => {
        const overlappingItemNumbers = incoming.itemNumbers.filter(
          (incomingItemNum) => other.itemNumbers.includes(incomingItemNum)
        );

        allOverlappingItemNumbers.push(...overlappingItemNumbers);
        return (
          other.sourcingStatusId ===
            SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
          other.itemNumbers.length > 0 &&
          overlappingItemNumbers.length > 0
        );
      });

      const uniqueOverlappingItemNumbers = Array.from(
        new Set(allOverlappingItemNumbers)
      );

      if (
        !resolutionChoices[TaggingValidationType.EXISTING_VENDOR_TAGGED] &&
        groupsOverlappingItemsVendorTagged.length > 0
      ) {
        // No resolution choice made yet, throw validation error and choice
        const validationType = TaggingValidationType.EXISTING_VENDOR_TAGGED;
        this.taggingValidation.next({
          type: validationType,
          incomingTaggedGroup: incomingTaggedGroup,
          existingTaggedGroupRef: existingTaggedGroupRef,
          displayAffected: uniqueOverlappingItemNumbers.join(', '),
        });
        return { isSucess: false, validationType: validationType };
      } else if (
        resolutionChoices[TaggingValidationType.EXISTING_VENDOR_TAGGED] ===
        TaggingResolutionChoice.REMOVE_AFFECTED
      ) {
        // Handle resolution choice

        // TODO: check if other tagged group, after removing overlapping and is empty array, should remove the tagged group?
        // Remove overlapping item numbers from any active own tagged groups with vendor tagged
        const unaffected: TaggedGroupVendorNum[] = [];
        const affected: TaggedGroupVendorNum[] = [];
        for (const group of tempActiveGroups) {
          if (
            this.isCurrentPETaggedGroup(group) &&
            group.sourcingStatusId ===
              SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
            group.itemNumbers.some((itemNumber) =>
              uniqueOverlappingItemNumbers.includes(itemNumber)
            )
          ) {
            const newGroup = {
              ...group,
              itemNumbers: group.itemNumbers.filter(
                (itemNumber) =>
                  !uniqueOverlappingItemNumbers.includes(itemNumber)
              ),
              groupStatus:
                group.groupStatus === GroupStatus.NEW
                  ? GroupStatus.NEW
                  : GroupStatus.UPDATED,
            };
            affected.push(newGroup);
          } else {
            unaffected.push(group);
          }
        }

        // Sort affected in front
        tempActiveGroups = [...affected, ...unaffected];
      }
    }

    // If incoming tagged group has vendors tagged and vendors exactly match other own tagged groups' tagged vendors
    // Validation to let user choose how to resolve duplicated vendor set
    if (
      incoming.vendors &&
      incoming.vendors.length > 0 &&
      incoming.itemNumbers.length > 0
    ) {
      const groupsWithSameVendors = currentPE.filter((other) => {
        return (
          other.sourcingStatusId ===
            SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
          _.isEqual(other.vendorNumbers, incoming.vendorNumbers)
        );
      });

      if (
        !resolutionChoices[TaggingValidationType.DUPLICATED_VENDOR_SET] &&
        groupsWithSameVendors.length > 0
      ) {
        // No resolution choice made yet, throw validation error and choice
        const validationType = TaggingValidationType.DUPLICATED_VENDOR_SET;
        this.taggingValidation.next({
          type: validationType,
          incomingTaggedGroup: incomingTaggedGroup,
          existingTaggedGroupRef: existingTaggedGroupRef,
          conflictingTaggedGroups: groupsWithSameVendors,
        });

        return { isSuccess: false, validationType: validationType };
      } else if (
        resolutionChoices[TaggingValidationType.DUPLICATED_VENDOR_SET] ===
        TaggingResolutionChoice.REPLACE
      ) {
        // Handle resolution choice
        // Incoming tagged group will replace existing tagged groups with same vendor set
        const unaffected: TaggedGroupVendorNum[] = [];
        for (const group of tempActiveGroups) {
          if (
            this.isCurrentPETaggedGroup(group) &&
            group.sourcingStatusId ===
              SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
            _.isEqual(group.vendorNumbers, incoming.vendorNumbers)
          ) {
            const { vendorNumbers, ...newGroup } = {
              ...group,
              groupStatus: GroupStatus.DELETED,
            };
            inactiveGroups.push(newGroup);
          } else {
            unaffected.push(group);
          }
        }

        tempActiveGroups = [...unaffected];
      } else if (
        resolutionChoices[TaggingValidationType.DUPLICATED_VENDOR_SET] ===
        TaggingResolutionChoice.MERGE
      ) {
        // Handle resolution choice
        // Incoming tagged group will be merged (in terms of item numbers) into existing tagged groups with same vendor set
        isNotAddIncoming = true;

        const unaffected: TaggedGroupVendorNum[] = [];
        const affected: TaggedGroupVendorNum[] = [];

        for (const group of tempActiveGroups) {
          if (
            this.isCurrentPETaggedGroup(group) &&
            group.sourcingStatusId ===
              SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
            _.isEqual(group.vendorNumbers, incoming.vendorNumbers)
          ) {
            const newGroup = this.sortInsideItemVendor({
              ...group,
              // Merge item numbers, get unique
              itemNumbers: Array.from(
                new Set([...group.itemNumbers, ...incoming.itemNumbers])
              ),
              groupStatus:
                group.groupStatus === GroupStatus.NEW
                  ? GroupStatus.NEW
                  : GroupStatus.UPDATED,
            });
            affected.push(newGroup);
          } else {
            unaffected.push(group);
          }
        }

        tempActiveGroups = [...affected, ...unaffected];
      }
    }

    /** Other system handling logic  */
    // If incoming is not tagging to vendor, merge incoming with any existing tagged group having same sourcing status
    if (!isIncomingVendorTagged) {
      const unaffected: TaggedGroupVendorNum[] = [];
      const affected: TaggedGroupVendorNum[] = [];

      for (const group of tempActiveGroups) {
        if (
          this.isCurrentPETaggedGroup(group) &&
          group.sourcingStatusId === incoming.sourcingStatusId
        ) {
          const newGroup = this.sortInsideItemVendor({
            ...group,
            itemNumbers: Array.from(
              new Set([...group.itemNumbers, ...incoming.itemNumbers])
            ),
            groupStatus:
              group.groupStatus === GroupStatus.NEW
                ? GroupStatus.NEW
                : GroupStatus.UPDATED,
          });
          isNotAddIncoming = true;
          affected.push(newGroup);
        } else {
          unaffected.push(group);
        }
      }

      tempActiveGroups = [...affected, ...unaffected];
    }

    if (incoming.itemNumbers.length > 0) {
      const unaffected: TaggedGroupVendorNum[] = [];
      const affected: TaggedGroupVendorNum[] = [];

      for (const group of tempActiveGroups) {
        // If tagged group is not tagged to any vendor and sourcing status does not match incoming,
        // and incoming tagged group items exist in this group, automatically remove overlapping item from this other tagged group
        if (
          this.isCurrentPETaggedGroup(group) &&
          group.sourcingStatusId !==
            SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
          group.sourcingStatusId !== incoming.sourcingStatusId &&
          group.itemNumbers.some((itemNumber) =>
            incoming.itemNumbers.includes(itemNumber)
          )
        ) {
          const itemNumbersExclIncoming = group.itemNumbers.filter(
            (itemNumber) => !incoming.itemNumbers.includes(itemNumber)
          );
          if (itemNumbersExclIncoming.length > 0) {
            // One or more items left after removing overlapping -> update group
            affected.push({
              ...group,
              itemNumbers: itemNumbersExclIncoming,
              groupStatus:
                group.groupStatus === GroupStatus.NEW
                  ? GroupStatus.NEW
                  : GroupStatus.UPDATED,
            });
          } else if (itemNumbersExclIncoming.length === 0) {
            // No more items left after removing overlapping -> delete group
            const { vendorNumbers, ...newGroup } = {
              ...group,
              itemNumbers: [],
              groupStatus: GroupStatus.DELETED,
            };
            inactiveGroups.unshift(newGroup);
          }
        } else {
          unaffected.push(group);
        }
      }

      tempActiveGroups = [...affected, ...unaffected];
    }

    /** Compute resulting tagged groups  */
    // Remove working vendorNumbers key
    const tempActiveGroupsClean = tempActiveGroups.map((group) => {
      const { vendorNumbers, ...newGroup } = group;
      return newGroup;
    });

    if (isNotAddIncoming) {
      let resultingTaggedGroups = existingTaggedGroupRef
        ? [
            ...tempActiveGroupsClean,
            { ...existingTaggedGroupRef, groupStatus: GroupStatus.DELETED },
            ...inactiveGroups,
          ]
        : [...tempActiveGroupsClean, ...inactiveGroups];

      return {
        isSuccess: true,
        resultingTaggedGroups: resultingTaggedGroups,
      };
    }

    const { vendorNumbers, ...incomingClean } = incoming;
    const incomingToAdd: TaggedGroup = this.sortInsideItemVendor({
      ...incomingClean,
      // Merge existingTaggedGroup if updating, else use default values when adding new
      taggedGroupId: existingTaggedGroupRef?.taggedGroupId ?? null,
      groupStatus:
        existingTaggedGroupRef &&
        existingTaggedGroupRef.groupStatus !== GroupStatus.NEW
          ? GroupStatus.UPDATED
          : GroupStatus.NEW,
    });

    return {
      isSuccess: true,
      resultingTaggedGroups: [
        incomingToAdd,
        ...tempActiveGroupsClean,
        ...inactiveGroups,
      ],
    };
  }

  private splitTaggedGroupsByPEs<
    T extends TaggedGroups['taggedGroups'] | TaggedGroupVendorNum[]
  >(taggedGroups: T) {
    const currentPE = [];
    const otherPEs = [];

    for (const taggedGroup of taggedGroups) {
      if (this.isCurrentPETaggedGroup(taggedGroup)) {
        currentPE.push(taggedGroup);
      } else {
        otherPEs.push(taggedGroup);
      }
    }

    return { currentPE: currentPE as T, otherPEs: otherPEs as T };
  }

  private computeVendorNumbersInTaggedGroups(
    taggedGroups: TaggedGroups['taggedGroups']
  ): TaggedGroupVendorNum[] {
    return taggedGroups.map((group) => {
      return {
        ...group,
        vendorNumbers:
          group.vendors?.map((vendor) => vendor.vendorNumber) ?? null,
      };
    });
  }

  public deleteTaggedGroup(existingTaggedGroupRef: TaggedGroup) {
    const { MPDocumentNumber, taggedGroups } = this.taggedGroups.value;

    const otherTaggedGroups = this.getOtherTaggedGroups(
      existingTaggedGroupRef,
      taggedGroups
    );

    if (existingTaggedGroupRef.groupStatus === GroupStatus.NEW) {
      // Does not exist in database
      this.taggedGroups.next({
        MPDocumentNumber,
        taggedGroups: otherTaggedGroups,
      });
    } else {
      // Exist in database
      const taggedGroupToAdd = {
        ...existingTaggedGroupRef,
        groupStatus: GroupStatus.DELETED,
      };
      this.taggedGroups.next({
        MPDocumentNumber,
        taggedGroups: [taggedGroupToAdd, ...otherTaggedGroups],
      });
    }
  }

  public get tempTagGroupValue() {
    return this.tempTagGroup.value;
  }

  public setTempTagGroup(newTempTagGroup: TaggedGroup) {
    this.tempTagGroup.next(_.cloneDeep(newTempTagGroup));
  }

  public updateTempTagGroup(partialTagGroup: Partial<TaggedGroup>) {
    let tagGroup = this.tempTagGroupValue;
    if (!tagGroup) {
      tagGroup = this.getNewTagGroup();
    }
    const updatedTagGroup = { ...tagGroup, ...partialTagGroup };
    this.setTempTagGroup(updatedTagGroup);

    return updatedTagGroup;
  }

  public resetTempTagGroup() {
    const newTagGroup = this.getNewTagGroup();

    this.tempTagGroup.next(newTagGroup);

    return newTagGroup;
  }

  public getTaggedGroupsForPosting(): TaggedGroupsForPosting {
    const taggedGroups = this.taggedGroupsValue.taggedGroups;
    const itemList = this.form?.ItemList;

    const invalid: TaggedGroupsForPosting['invalidTaggedGroups'] = [];
    const valid: TaggedGroupsForPosting['validTaggedGroups'] = [];

    for (const taggedGroup of taggedGroups) {
      const {
        itemNumbers,
        purchaseExecutive,
        sourcingStatusId,
        vendors,
        groupStatus,
      } = taggedGroup;

      if (!this.isCurrentPETaggedGroup(taggedGroup)) {
        // Skip taggedGroup not belonging to current PurchaseExecutive
        continue;
      }

      if (
        groupStatus === GroupStatus.PERSISTED_INACTIVE ||
        groupStatus === GroupStatus.DELETED
      ) {
        // Skip validations, add to posting
        valid.push(taggedGroup);
        continue;
      }

      if (itemNumbers.length === 0) {
        // TaggedGroup has empty itemNumbers, which is required
        invalid.push({
          taggedGroup,
          invalidReason: InvalidTaggedGroupReason.EMPTY_ITEM_NUMBERS,
        });
        continue;
      }

      const taggedItems =
        itemList?.filter((item) =>
          itemNumbers.includes(item.AddItems.displayItemNumber)
        ) ?? [];
      const activeTaggedItems = taggedItems.filter(
        (item) => !item.isDeleted && !item.Strikethrough
      );

      if (
        activeTaggedItems.length === 0 ||
        activeTaggedItems.length < itemNumbers.length
      ) {
        // TaggedGroup has itemNumbers associated with deleted/strikedthrough or missing items
        invalid.push({
          taggedGroup,
          invalidReason:
            InvalidTaggedGroupReason.TAGGED_TO_MISSING_DELETED_ITEM,
        });
        continue;
      }

      const assignedTaggedItems = activeTaggedItems.filter(
        (item) => item.AddItems.PurchaseExecutive === purchaseExecutive
      );

      if (assignedTaggedItems.length < itemNumbers.length) {
        // TaggedGroup has itemNumbers assigned to another Purchase Executive
        // (current Purchase Executive can only tag items assigned to him)
        invalid.push({
          taggedGroup,
          invalidReason: InvalidTaggedGroupReason.ITEM_ASSIGNED_TO_ANOTHER_PE,
        });
        continue;
      }

      if (!sourcingStatusId) {
        // Sourcing Status is missing from TaggedGroup
        invalid.push({
          taggedGroup,
          invalidReason: InvalidTaggedGroupReason.MISSING_SOURCING_STATUS,
        });
        continue;
      }

      if (
        sourcingStatusId === SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED &&
        (!vendors || vendors.length === 0)
      ) {
        // Selected has vendors to tag, but did not tag any vendor
        invalid.push({
          taggedGroup,
          invalidReason: InvalidTaggedGroupReason.MISSING_VENDORS,
        });
        continue;
      }

      const vendorNumbers = vendors?.map((vendor) => vendor.vendorNumber) ?? [];
      const activeVendors =
        this.form?.VendorsToSource.filter(
          (vendor) =>
            vendor.VendorNumber &&
            !vendor.isDeleted &&
            !vendor.Strikethrough &&
            vendorNumbers.includes(vendor.VendorNumber)
        ) ?? [];

      if (Array.isArray(vendors) && activeVendors.length < vendors.length) {
        // TaggedGroup has vendors associated with deleted/strikedthrough or missing vendors
        invalid.push({
          taggedGroup,
          invalidReason:
            InvalidTaggedGroupReason.TAGGED_TO_MISSING_DELETED_VENDOR,
        });
        continue;
      }

      valid.push(taggedGroup);
    }

    return { invalidTaggedGroups: invalid, validTaggedGroups: valid };
  }

  public get hasTaggedGroupsChanged() {
    return (
      this.taggedGroups.value.taggedGroups.length > 0 &&
      this.taggedGroups.value.taggedGroups.some((taggedGroup) =>
        [GroupStatus.NEW, GroupStatus.UPDATED, GroupStatus.DELETED].includes(
          taggedGroup.groupStatus
        )
      )
    );
  }

  public validateAllowPEReassignment(
    MPDocumentNumber: string
  ): TaggingImmediateValidationResult {
    const taggedGroupsObj = this.taggedGroupsValue;

    if (
      taggedGroupsObj.MPDocumentNumber === MPDocumentNumber &&
      this.hasTaggedGroupsChanged
    ) {
      this.taggingValidation.next({
        type: TaggingValidationType.REASSIGNMENT_UNSAVED_TAG,
      });
      return { isSuccess: false };
    }

    return { isSuccess: true };
  }

  public validateAllowVendorDelete(
    VendorNumberToDelete: string
  ): TaggingImmediateValidationResult {
    // Active tagged groups belonging to current PE or other PEs
    const allActiveTaggedGroups = this.getActiveTaggedGroups(
      this.taggedGroups.value.taggedGroups
    );

    const taggedGroupWithDeletingVendor = allActiveTaggedGroups.find(
      (group) =>
        Array.isArray(group.vendors) &&
        group.vendors.some(
          (vendor) => vendor.vendorNumber === VendorNumberToDelete
        )
    );

    // Vendor to delete is not in any active tagged group
    if (!taggedGroupWithDeletingVendor) {
      return { isSuccess: true };
    }

    // Differentiate if conflicting tagged group belongs to current PE or other PEs
    const validationType = this.isCurrentPETaggedGroup(taggedGroupWithDeletingVendor)
      ? TaggingValidationType.DELETE_VENDOR_OWN_TAG_CONFLICT
      : TaggingValidationType.DELETE_VENDOR_OTHER_PURCHASE_EXECUTIVE_TAG_CONFLICT;

    // Display validation error
    this.taggingValidation.next({
      type: validationType,
    });

    return { isSuccess: false, validationType: validationType };
  }

  public getActiveTaggedGroups(taggedGroups: TaggedGroups['taggedGroups']) {
    return taggedGroups.filter(
      (taggedGroup) =>
        ![GroupStatus.DELETED, GroupStatus.PERSISTED_INACTIVE].includes(
          taggedGroup.groupStatus
        )
    );
  }

  public getInactiveTaggedGroups(taggedGroups: TaggedGroups['taggedGroups']) {
    return taggedGroups.filter((taggedGroup) =>
      [GroupStatus.DELETED, GroupStatus.PERSISTED_INACTIVE].includes(
        taggedGroup.groupStatus
      )
    );
  }

  private isCurrentPETaggedGroup(taggedGroup: TaggedGroup) {
    return taggedGroup.purchaseExecutive === this.loggedInUsername;
  }

  private sortInsideItemVendor<T extends TaggedGroup | TaggedGroupVendorNum>(
    taggedGroup: T
  ): T {
    return {
      ...taggedGroup,
      itemNumbers: _.sortBy(taggedGroup.itemNumbers),
      vendors: taggedGroup.vendors
        ? _.sortBy(taggedGroup.vendors, ['vendorName'])
        : null,
    };
  }

  private getNewTagGroup() {
    const newTagGroup = _.cloneDeep(NEW_TAG_GROUP);

    // If loggedInUsername not found, purchaseExecutive will be empty
    if (this.loggedInUsername) {
      newTagGroup.purchaseExecutive = this.loggedInUsername;
    }
    return newTagGroup;
  }

  private getOtherTaggedGroups(
    oldTaggedGroup: TaggedGroup,
    taggedGroups: TaggedGroups['taggedGroups']
  ) {
    const otherTaggedGroups = taggedGroups.filter((group) => {
      // Deep object (& nested array) comparison
      return !_.isEqual(group, oldTaggedGroup);
    });

    return otherTaggedGroups;
  }

  private getItemSourcingStatusMap(taggedGroups: TaggedGroups['taggedGroups']) {
    const activeTaggedGroups = this.getActiveTaggedGroups(taggedGroups).filter(
      (taggedGroup) => this.isCurrentPETaggedGroup(taggedGroup)
    );

    const map: DisplayItemNumberToSourcingStatusIdMap = new Map();
    for (const taggedGroup of activeTaggedGroups) {
      const { itemNumbers, sourcingStatusId } = taggedGroup;

      // Skip tagged group with undefined sourcingStatusId
      if (!sourcingStatusId) {
        continue;
      }

      for (const itemNumber of itemNumbers) {
        map.set(itemNumber, sourcingStatusId);
      }
    }

    return map;
  }

  // to move to util
  private compareArrays(array1: any[] | null, array2:any[]| null): boolean {
    // if 1 or 2 are null, return false
    if(!array1 || !array2) return false;

    return array1.length === array2.length &&
      array1.every((obj1, index) => 
        isEqual(obj1, array2[index])
      );
  }

  deepEqual(obj1: any, obj2: any) {
    // Check if both object are equal
    if (obj1 === obj2) return true;

    // Check if param are both objects
    if (typeof obj1 !== 'object' || obj1 === null || 
        typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    // Get keys of both objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if the number of keys is the same
    if (keys1.length !== keys2.length) return false;

    // Check each key and value
    for (const key of keys1) {
        if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
  }

  deepCompareArrays(arr1:any, arr2: any) {
    // Check if both are the same
    if (arr1 === arr2) return true;

    // Check if both are arrays
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;

    // Check if the lengths are the same
    if (arr1.length !== arr2.length) return false;

    // Check each object in the arrays
    for (const obj1 of arr1) {
        if (!arr2.some(obj2 => this.deepEqual(obj1, obj2))) {
            return false;
        }
    }

    return true;
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
